<template>
    <div>
        <div class="mb-8">
            <div class="w-full flex flex-col shadow-md bg-white rounded">
                <div
                    class="flex flex-row rounded-t p-2 justify-between">
                    <span class="pl-2">{{ (contact ? 'Recent Projects' : (isChildProjectsTab ? 'Child Company Projects' : 'Recent Projects')) }}</span>

                    <div class="flex flex-row">
                        <div class="bg-grey-lighter px-3 rounded-full mr-3">
                            <input ref="filter" type="search"
                                   class="bg-grey-lighter focus:outline-none rounded text-xs text-grey-darkest"
                                   placeholder="Search Projects" @input="debounceFilterProjects"
                                   v-model="projectFilter">
                            <i v-if="loadingProjects" class="fa fa-spin fa-spinner text-grey-darkest"
                               aria-hidden="true"></i>
                        </div>
                        <button v-if="userRole !== 'view_only'" @click="addProject"
                                class="border-2 border-followup-nav focus:outline-none hover:bg-followup-nav rounded-full">
                            <span class="font-semibold hover:text-white material-icons p-1 text-followup-nav text-xs">add</span>
                        </button>

                    </div>
                </div>
                <div class="table-responsive p-3">
                    <table class="text-left w-full table-bordered" :id="(contact ? 'contactRecentProjects' : (isChildProjectsTab ? 'companyChildRecentProjects' : 'companyRecentProjects'))">
                    <div>
                        <thead>
                        <tr>
                            <th v-for="(column,index) in columnsToShow"
                                class="py-1 px-1 bg-grey-lightest font-bold uppercase text-xs text-grey-darkest border-b border-grey-light whitespace-no-wrap md:table-cell"
                                :class="([(contact ? 'column-header-resize-contact-projects' : (isChildProjectsTab ? 'column-header-resize-company-child-projects' : 'column-header-resize-company-projects'))])"
                                style="width:9%" v-if="checkFieldLabelExist(column.property_name)">
                                <div class="column-resize" :class="([(`${column.property_name}`), (contact ? 'contact-projects-resize' : (isChildProjectsTab ? 'company-child-projects-resize' : 'company-projects-resize'))])">
                                    <button v-if="availableSortingColumns[column.property_name]"
                                            class="font-bold uppercase text-xs text-grey-darkest hover:text-teal hover:no-underline focus:outline-none"
                                            :class="currentProjectOrder.property === column.property_name ? 'text-teal' : ''"
                                            @click="sortByColumn(column.property_name)">
                                        {{ getColumnFieldPropertyName(column.name) }}
                                        <i class="fa fa-xs ml-2" :class="{'fa-chevron-up' : currentProjectOrder.direction === 'asc',
                                        'fa-chevron-down' : currentProjectOrder.direction === 'desc'}"
                                           v-if="currentProjectOrder.property === column.property_name"></i>
                                    </button>
                                    <button v-else
                                            class="font-bold uppercase text-xs text-grey-darkest hover:text-teal hover:no-underline focus:outline-none">
                                        {{ column.name }}
                                    </button>
                                </div>
                            </th>
                            <th class="py-1 px-3 bg-grey-lightest font-bold uppercase text-xs text-grey-darkest border-b border-grey-light hidden xl:table-cell md:table-cell"
                                :class="([(contact ? 'column-header-resize-contact-projects' : (isChildProjectsTab ? 'column-header-resize-company-child-projects' : 'column-header-resize-company-projects'))])"
                                style="width:9%">
                                <div class="column-resize" :class="([(contact ? 'contact-projects-resize' : (isChildProjectsTab ? 'company-child-projects-resize' : 'company-projects-resize'))])">
                                    <button
                                        class="font-bold uppercase text-xs text-grey-darkest hover:text-teal hover:no-underline focus:outline-none"
                                        :class="currentProjectOrder.property === 'last_update' ? 'text-teal' : ''"
                                        @click="sortByColumn('last_update')">
                                        Last Update
                                        <i class="fa fa-xs ml-2"
                                           :class="{'fa-chevron-up' : currentProjectOrder.direction === 'asc',
                                        'fa-chevron-down' : currentProjectOrder.direction === 'desc'}"
                                           v-if="currentProjectOrder.property === 'last_update'"></i>
                                    </button>
                                </div>
                            </th>
                            <th class="py-1 px-2 border-b border-grey-light">
                                <project-column-state-management
                                    :name="(contact ? 'contact-projects' : (isChildProjectsTab ? 'company-child-projects' : 'company-projects'))"
                                    :state="columns"
                                    :field-labels="fieldLabels"
                                    :recordDetailFieldLabels="recordDetailFieldLabels"
                                />
                            </th>
                        </tr>
                        </thead>
                        <tbody class="bg-white">
                        <tr @click="editProject(project)"
                            class="cursor-pointer hover:bg-grey-lightest border-b border-grey-light"
                            v-for="project in projects">
                            <td v-for="column in columnsToShow"  v-if="checkFieldLabelExist(column.property_name)"
                                class="pl-2 border-b border-grey-light text-xs hidden xl:table-cell md:table-cell">
                                <div v-if="!column.is_sales_behavior && !column.is_custom_field">
                                    <span v-if="column.property_name === 'base_bid' || column.property_name === 'contract_amount'">
                                        {{ project[column.property_name + '_formatted'] }}
                                    </span>
                                    <span v-else-if="datesColumns.includes(column.property_name)">
                                        {{ getFormattedDate(project[column.property_name]) }}
                                    </span>
                                    <span v-else-if="column.property_name == 'sales_statuses_code'"
                                        :style="{'background-color':project['status_background_color'], 'border' : project['status_border_color'] + ' 1px solid'}"
                                        class="appearance-none p-1 border-none focus:outline-none rounded text-grey-darkest text-xs w-full d-block"
                                        >
                                        {{ project[column.property_name] }}
                                    </span>
                                    <span v-else>
                                        {{ project[column.property_name] }}
                                    </span>
                                </div>
                                <div v-if="column.is_sales_behavior">
                                    {{ getSalesBehaviorValue(project.sales_behaviors_data, column.sales_behavior_id) }}
                                </div>
                                <div v-if="column.is_custom_field && !column.hasOwnProperty('is_record_detail_custom_field')">
                                    {{ getCustomFieldValue(project.custom_field_data, column.custom_field_id) }}
                                </div>
                                <div v-if="column.is_custom_field && column.hasOwnProperty('is_record_detail_custom_field')">
                                    {{ getCustomFieldValue(project.record_detail_custom_field_data, column.custom_field_id) }}
                                </div>
                            </td>
                            <td class="pl-2 border-b border-grey-light text-xs hidden xl:table-cell md:table-cell">
                                <div>{{ getFormattedDate(project.last_update) }}</div>
                            </td>
                            <td></td>
                        </tr>
                        </tbody>
                    </div>
                </table>
                </div>
            </div>
            <div class="flex justify-center mt-3" v-if="projectPagination.meta.total > projectPagination.meta.per_page">
                <b-pagination
                    @change="goToPage"
                    v-model="projectPagination.meta.current_page"
                    :total-rows="projectPagination.meta.total"
                    :per-page="projectPagination.meta.per_page"
                    first-text="First"
                    prev-text="Prev"
                    next-text="Next"
                    last-text="Last"
                ></b-pagination>

            </div>
        </div>
    </div>
</template>

<script>

import Form from "../../utils/form";
import Multiselect from 'vue-multiselect';
import ProjectColumnStateManagement from "../shared/project-column-state-management.vue";
import axios from "axios";
let autocomplete=null;
export default {
    name: 'recent-projects',
    props: ['company', 'masks', 'symbols', 'relatedCompanies', 'canEdit', 'fieldLabels', 'userRole', 'companyProjectState', 'companyProjectAvailableColumns', 'calendar', 'companyProjectAvailableSort', 'companyChildProjectAvailableColumns', 'isChildProjectsTab', 'companyChildProjectState', 'contact', 'contactProjectState', 'contactProjectAvailableSort', 'contactProjectAvailableColumns',
            'recordDetailFieldLabels'],
    components: {ProjectColumnStateManagement, Multiselect},

    data() {
        let projectState = [];
        let childProjectState = [];
        let contactProjectState = [];

        if(this.companyProjectState) {
            projectState = this.companyProjectState;
            projectState = projectState.filter((state) => {
                return (state.property_name !== "age" && state.property_name !== "meeting_date")
            });
        }
        if(this.companyChildProjectState) {
            childProjectState = this.companyChildProjectState;
            childProjectState = childProjectState.filter((state) => {
                return (state.property_name !== "age" && state.property_name !== "meeting_date")
            });
        }
        if(this.contactProjectState) {
            contactProjectState = this.contactProjectState;
            contactProjectState = contactProjectState.filter((state) => {
                return (state.property_name !== "age" && state.property_name !== "meeting_date")
            });
        }

        return {
            currentProjectOrder: {
                property: 'last_update',
                direction: 'desc'
            },
            projects: null,
            projectPagination: {
                data: [],
                meta: {},
                links: {},
            },
            projectFilter: '',
            columns: (this.contact ? contactProjectState : (this.isChildProjectsTab ? childProjectState : projectState)),
            columnsToShow: [],
            datesColumns: ['contract_date', 'login_date', 'dead_date', 'pre_bid', 'bid_date'],
            companyProjectWidth : null,
            companyChildProjectWidth : null,
            contactProjectWidth : null,
            availableSortingColumns: {},
            loadingProjects: false
        }
    },
    created() {
        this.fetchData();
        this.getWidth();
    },
    mounted() {
        let self = this;
        self.columnsToShow = (self.contact ? self.contactProjectAvailableColumns : (self.isChildProjectsTab ? self.companyChildProjectAvailableColumns : self.companyProjectAvailableColumns));
        this.availableSortingColumns = self.contact ? self.contactProjectAvailableSort : self.companyProjectAvailableSort;
        Bus.$on('saveStateColumns', function(data) {
            self.updateStateColumns(data);
        })
    },
    methods: {
        fetchData() {
            this.fetchProjects();
        },

        goToPage(page) {
            this.projectPagination.meta.current_page = page;
            this.fetchProjects();
        },

        // fetchProjects() {
        //     this.loadingProjects = true;
        //     axios.get(route('api.company.projects', {
        //         company: this.company.id,
        //         filter: this.projectFilter,
        //         page: this.projectPagination.meta.current_page,
        //         sortBy: this.currentProjectOrder.property,
        //         direction: this.currentProjectOrder.direction
        //     })).then(({data}) => {
        //         this.projects = data.data;
        //         this.projectPagination = data;
        //         this.loadingProjects = false;
        //     });
        // },
        fetchProjects() {
            let params = {
                page: this.projectPagination.meta.current_page,
                sortBy: this.currentProjectOrder.property,
                direction: this.currentProjectOrder.direction,
                columns: (this.contact ? (this.contactProjectAvailableColumns ? this.contactProjectAvailableColumns.map(obj => obj.property_name) : []) : (this.isChildProjectsTab ? (this.companyChildProjectAvailableColumns ? this.companyChildProjectAvailableColumns.map(obj => obj.property_name) : []) : (this.companyProjectAvailableColumns ? this.companyProjectAvailableColumns.map(obj => obj.property_name) : []))),
                company: this.company.id,
                contactId: this.contact && this.contact.id ? this.contact.id : null,
                filter: this.projectFilter,
                child: this.isChildProjectsTab ? this.isChildProjectsTab : false
            };
            this.loadingProjects = true;
            axios.get(route('api.company.projects', params)).then(({data}) => {
                this.projects = data.data;
                this.allProjectIds= data.meta.allProjectIds;
                this.currentProjectIds= data.meta.currentProjectIds;
                this.projectPagination= data;
                this.loadingProjects = false;
            });
        },

        debounceFilterProjects: _.debounce(function (e) {
            // this.currentProjectOrder = {property: '', direction: ''}
            this.projectFilter = e.target.value;
            this.fetchProjects()
        }, 500),


        addProject() {
            if (this.contact){
                window.location = route('project.create', {company_id: this.company.id,contact_id:this.contact.id});
            }else {
                window.location = route('project.create', {company_id: this.company.id});
            }
        },

        editProject(project) {
            window.location = route('project.edit', {project: project.id, company_id: this.company.id});
        },

        sortByColumn(sortBy) {
            if (sortBy === this.currentProjectOrder.property) {
                this.currentProjectOrder.direction = (this.currentProjectOrder.direction === 'asc') ? 'desc' : 'asc'
            } else {
                this.currentProjectOrder.property = sortBy;
                this.currentProjectOrder.direction = 'desc';
            }
            this.fetchProjects();
        },
        updateStateColumns(data) {
            axios.post(route('api.company-contact.project.update-state'), {state: data.columns, type: data.name}).then(response => {
                this.getToast().fire({
                    type: 'success',
                    title: 'State updated successfully'
                });
                window.location.reload();
            });
        },
        checkFieldLabelExist(labelName) {
            switch (labelName) {
                case 'office':
                    return this.fieldLabels.hasOwnProperty('office');
                case 'sales_statuses_code':
                    return this.fieldLabels.hasOwnProperty('sales-status');
                case 'pre_bid':
                    return this.fieldLabels.hasOwnProperty('pre-bid');
                case 'bid_date':
                    return this.fieldLabels.hasOwnProperty('bid-date');
                case 'marketing_status':
                    return this.fieldLabels.hasOwnProperty('marketing-status');
                case 'lead_from':
                    return this.fieldLabels.hasOwnProperty('lead-source');
                case 'client_profile':
                    return this.fieldLabels.hasOwnProperty('client-profile');
                case 'contract_type':
                    return this.fieldLabels.hasOwnProperty('contract-type');
                case 'products_and_services':
                    return this.fieldLabels.hasOwnProperty('products-and-services');
                case 'estimator_name':
                    return this.fieldLabels.hasOwnProperty('estimator');
                case 'account_manager_name':
                    return this.fieldLabels.hasOwnProperty('account-manager');
                case 'take_off_person_name':
                    return this.fieldLabels.hasOwnProperty('take-off-person');
                default:
                    return true;
            }
        },
        getSalesBehaviorValue(salesBehaviorData, fieldId) {
            let dateFormat = this.calendar ? this.masks.moment_mask + ' hh:mm A' : this.masks.moment_mask;
            for (var i = 0; i < salesBehaviorData.length; i++) {
                if (salesBehaviorData[i].id === fieldId) {
                    return salesBehaviorData[i].value ? moment.utc(salesBehaviorData[i].value).local().format(dateFormat) : null;
                }
            }
        },
        getCustomFieldValue(customFieldsData, fieldId) {
            for(let i = 0; i < customFieldsData.length; i++) {
                if(customFieldsData[i].id == fieldId) {
                    if(customFieldsData[i].type === 'DATE') {
                        return customFieldsData[i].valueLabel ? moment.utc(customFieldsData[i].valueLabel).format(this.masks.moment_mask) : null;
                    }
                    else {
                        return customFieldsData[i].valueLabel;
                    }
                }
            }
        },
        getFormattedDate(value) {
            return value ? moment(value).format(this.masks.moment_mask) : null;
        },
        getWidth() {
            let self = this;
            self.fetchWidth();
            $(window).mouseup((event) => {
                let widthCls = $(event.target).attr('class');
                let words = '';
                let cls1 = '';
                if(widthCls.includes('column-resize')) {
                    words = widthCls.split(' ');
                    cls1 = words[1];
                    if(self.contact && widthCls.includes('contact-projects-resize')) {
                        if(self.contactProjectWidth && Object.keys(self.contactProjectWidth).length > 0) {
                            self.contactProjectWidth[cls1] = $('.' + cls1).width();
                            self.updateWidth(self.contactProjectWidth, 'contact-projects');
                        }
                        else {
                            let columnWidth = {};
                            columnWidth[cls1] = $('.' + cls1).width();
                            self.updateWidth(columnWidth, 'contact-projects', true);
                        }
                    }
                    else {
                        if(self.isChildProjectsTab && widthCls.includes('company-child-projects-resize')) {
                            if(self.companyChildProjectWidth && Object.keys(self.companyChildProjectWidth).length > 0) {
                                self.companyChildProjectWidth[cls1] = $('.' + cls1).width();
                                self.updateWidth(self.companyChildProjectWidth, 'company-child-projects');
                            }
                            else {
                                let columnWidth = {};
                                columnWidth[cls1] = $('.' + cls1).width();
                                self.updateWidth(columnWidth, 'company-child-projects', true);
                            }
                        }
                        else if(widthCls.includes('company-projects-resize')) {
                            if(self.companyProjectWidth && Object.keys(self.companyProjectWidth).length > 0) {
                                self.companyProjectWidth[cls1] = $('.' + cls1).width();
                                self.updateWidth(self.companyProjectWidth, 'company-projects');
                            }
                            else {
                                let columnWidth = {};
                                columnWidth[cls1] = $('.' + cls1).width();
                                self.updateWidth(columnWidth, 'company-projects', true);
                            }
                        }
                    }
                }
            });
        },
        updateWidth(width, type, fetch = false) {
            let self = this;
            axios.post(route('api.company-contact.project.update-width-size'),{widthSize: width, type: type}).then(res => {
                if(fetch) {
                    self.fetchWidth();
                }
            });
        },
        fetchWidth() {
            let self = this;
            axios.get(route('api.company-contact.project.get-width-size', {type: (self.contact ? 'contact-projects' : (self.isChildProjectsTab ? 'company-child-projects' : 'company-projects'))})).then(res => {
                if(self.contact) {
                    self.contactProjectWidth = res.data.widthSize;
                    if(self.contactProjectWidth && Object.keys(self.contactProjectWidth).length > 0) {
                        for(let i = 0; i < Object.keys(self.contactProjectWidth).length; i++) {
                            $('.' + Object.keys(self.contactProjectWidth)[i].toString()).width(Object.values(self.contactProjectWidth)[i].toString() + 'px');
                        }
                    }
                }
                else {
                    if(self.isChildProjectsTab) {
                        self.companyChildProjectWidth = res.data.widthSize;
                        if(self.companyChildProjectWidth && Object.keys(self.companyChildProjectWidth).length > 0) {
                            for(let i = 0; i < Object.keys(self.companyChildProjectWidth).length; i++) {
                                $('.' + Object.keys(self.companyChildProjectWidth)[i].toString()).width(Object.values(self.companyChildProjectWidth)[i].toString() + 'px');
                            }
                        }
                    }
                    else {
                        self.companyProjectWidth = res.data.widthSize;
                        if(self.companyProjectWidth && Object.keys(self.companyProjectWidth).length > 0) {
                            for(let i = 0; i < Object.keys(self.companyProjectWidth).length; i++) {
                                $('.' + Object.keys(self.companyProjectWidth)[i].toString()).width(Object.values(self.companyProjectWidth)[i].toString() + 'px');
                            }
                        }
                    }
                }
            });
        },
        getColumnFieldPropertyName(columnName) {
            const finalLabels = this.recordDetailColumnsList.includes(columnName.replace(/ /g, '-').toLowerCase()) ? this.recordDetailFieldLabels : this.fieldLabels;
            return this.getFieldLabel(finalLabels, columnName.replace(/ /g, '-').toLowerCase());
        },
    }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
#companyRecentProjects .fix-name-content {
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 10vw !important;
    display: block;
}
#companyRecentProjects {
    border-collapse: collapse;
    border-spacing: 0px;
    overflow: scroll;
}
#companyRecentProjects th.column-header-resize-company-projects {
    padding: 0;
    margin: 0px;
}
#companyRecentProjects th.column-header-resize-company-projects div.column-resize {
    resize: horizontal;
    overflow: auto;
    min-width: 90%;
    margin: 0px;
    padding: 0px;
    display: block;
    border: 0;
}
#companyRecentProjects td.td-text-overflow {
    overflow: hidden;
}
@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #companyRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}

@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (orientation: portrait)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #companyRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}

#companyChildRecentProjects .fix-name-content {
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 10vw !important;
    display: block;
}
#companyChildRecentProjects {
    border-collapse: collapse;
    border-spacing: 0px;
    overflow: scroll;
}
#companyChildRecentProjects th.column-header-resize-company-child-projects {
    padding: 0;
    margin: 0px;
}
#companyChildRecentProjects th.column-header-resize-company-child-projects div.column-resize {
    resize: horizontal;
    overflow: auto;
    min-width: 90%;
    margin: 0px;
    padding: 0px;
    display: block;
    border: 0;
}
#companyChildRecentProjects td.td-text-overflow {
    overflow: hidden;
}
@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #companyChildRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}

@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (orientation: portrait)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #companyChildRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}

#contactRecentProjects .fix-name-content {
    white-space: nowrap;
    text-overflow: ellipsis;
    width: 10vw !important;
    display: block;
}
#contactRecentProjects {
    border-collapse: collapse;
    border-spacing: 0px;
    overflow: scroll;
}
#contactRecentProjects th.-company-projects-contact-projects {
    padding: 0;
    margin: 0px;
}
#contactRecentProjects th.column-header-resize-contact-projects div.column-resize {
    resize: horizontal;
    overflow: auto;
    min-width: 90%;
    margin: 0px;
    padding: 0px;
    display: block;
    border: 0;
}
#contactRecentProjects td.td-text-overflow {
    overflow: hidden;
}
@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #contactRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}

@media only screen
and (min-device-width: 768px)
and (max-device-width: 1024px)
and (orientation: portrait)
and (-webkit-min-device-pixel-ratio: 1) {
    td > span {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
    #contactRecentProjects .fix-name-content {
        white-space: nowrap;
        width: auto !important;
        display: block;
    }
}
</style>
